import { useIsSSR } from 'react-aria';

import { SelectBase, SelectBaseProps } from '../SelectBase/SelectBase';

import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { SelectGroup } from './SelectGroup';
import { SelectItem } from './SelectItem';

import { ListBoxProps } from '@/SelectBase/ListBox';
import { Icon, VisuallyHidden } from '@zapier/design-system';

import styles from './Select.module.scss';

export type Key = string | number;

export type SelectProps<T extends object> = Omit<
  SelectBaseProps<T>,
  'children'
> & {
  children: ListBoxProps<T>['children'];
  /** Message to display when there is a validation error. `errored` must be `true` in order for the `errorMessage` to be displayed. */
  errorMessage?: string;
  /** Description to display below the Select. */
  helpText?: string;
  /** Visually hides the label when `true`. */
  isLabelHidden?: boolean;
  /** The content to label the Select. */
  label: string;
  /**
   * Sets the selected value. For use in a controlled component. To be used
   * alongside `onSelectionChange`.
   */
  selectedKey?: Key | null;
};

/**
 * Root component wrapping around the `Select` subcomponents. Built using React
 * Aria's [Select](https://react-spectrum.adobe.com/react-aria/Select.html)
 * component.
 */
export const Select = <T extends object>({
  children,
  errorMessage,
  helpText,
  isInvalid = false,
  isLabelHidden = false,
  label,
  size = 'medium',
  ...props
}: SelectProps<T>) => {
  const isSSR = useIsSSR();

  if (isSSR) {
    // react-aria Select renders hidden children in order to build the
    // collection even when the popover is not open. react-aria Hidden wrapps
    // its children in a `template` tag in SSR environments. The presence of
    // that `template` creates errors during hydration.
    //
    // https://github.com/adobe/react-spectrum/blob/17751bb75cf4b0500e86abe9e1b4561d140f9486/packages/react-aria-components/src/Select.tsx#L135
    //
    // https://github.com/adobe/react-spectrum/blob/main/packages/react-aria-components/src/utils.tsx#L326
    //
    // We return nothing while the component is being server side rendered or
    // hydrated on the client to prevent that error.
    return null;
  }

  return (
    <SelectBase {...props} isInvalid={isInvalid} size={size}>
      {isLabelHidden ? (
        <VisuallyHidden>
          <SelectBase.Label>{label}</SelectBase.Label>
        </VisuallyHidden>
      ) : (
        <SelectBase.Label>
          {isInvalid && (
            <Icon
              color="StatusErrorStronger"
              isBlock
              name="formXCircle"
              size={18}
            />
          )}
          {label}
        </SelectBase.Label>
      )}
      <SelectBase.TriggerButton>
        <SelectBase.Value />
        <Icon
          className={styles.dropdownIcon}
          name="arrowDropdown"
          color="TextWeakest"
          size={size === 'compact' || size === 'medium' ? 20 : 24}
        />
      </SelectBase.TriggerButton>
      {isInvalid && errorMessage ? (
        <ErrorMessage isCompact={size === 'compact'}>
          {errorMessage}
        </ErrorMessage>
      ) : (
        helpText && <SelectBase.Description>{helpText}</SelectBase.Description>
      )}
      <SelectBase.Popover>
        <SelectBase.ListBox>{children}</SelectBase.ListBox>
      </SelectBase.Popover>
    </SelectBase>
  );
};

Select.Item = SelectItem;
Select.Group = SelectGroup;
